import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { adminApi } from '@/services/api';
import { Loader2, Store, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from './shared';

const linesToList = (text) => String(text || '').split('\n').map((s) => s.trim()).filter(Boolean);
const listToLines = (arr) => (arr || []).join('\n');

const CardFields = ({ cards, onChange, labels = ['Kort 1', 'Kort 2', 'Kort 3'] }) => (
  <div className="space-y-4">
    {(cards || [{}, {}, {}]).slice(0, 3).map((card, i) => (
      <div key={i} className="p-4 border border-gray-100 rounded-xl bg-gray-50/50">
        <p className="text-xs font-bold text-gray-500 uppercase mb-3">{labels[i] || `Kort ${i + 1}`}</p>
        <div className="space-y-2">
          <Input
            placeholder="Rubrik"
            value={card.title || ''}
            onChange={(e) => onChange(i, 'title', e.target.value)}
          />
          <Textarea
            rows={2}
            placeholder="Beskrivning"
            value={card.description || ''}
            onChange={(e) => onChange(i, 'description', e.target.value)}
          />
        </div>
      </div>
    ))}
  </div>
);

export const PartnerSection = ({
  partnerSettings,
  setPartnerSettings,
  onSaveSettings,
  savingSettings,
  onStoreCreated,
  loading,
}) => {
  const [creating, setCreating] = useState(false);
  const [storeForm, setStoreForm] = useState({
    owner_name: '',
    owner_email: '',
    owner_password: '',
    store_name: '',
    location: '',
    county: '',
    org_number: '',
    bankgiro: '',
    contact_email: '',
    contact_phone: '',
    customer_phone: '',
    description: '',
    logo_url: '',
  });

  if (loading && !partnerSettings) return <EmptyState message="Laddar partnerinställningar…" />;
  if (!partnerSettings) return <EmptyState message="Kunde inte ladda partnerinställningar" />;

  const patchRoot = (field, value) => setPartnerSettings({ ...partnerSettings, [field]: value });
  const patchBli = (field, value) =>
    setPartnerSettings({
      ...partnerSettings,
      bli_partner: { ...(partnerSettings.bli_partner || {}), [field]: value },
    });
  const patchAnslut = (field, value) =>
    setPartnerSettings({
      ...partnerSettings,
      anslut: { ...(partnerSettings.anslut || {}), [field]: value },
    });

  const patchBliCards = (index, field, value) => {
    const cards = [...(partnerSettings.bli_partner?.offer_cards || [{}, {}, {}])];
    while (cards.length < 3) cards.push({});
    cards[index] = { ...cards[index], [field]: value };
    patchBli('offer_cards', cards);
  };

  const patchAnslutCards = (index, field, value) => {
    const cards = [...(partnerSettings.anslut?.why_cards || [{}, {}, {}])];
    while (cards.length < 3) cards.push({});
    cards[index] = { ...cards[index], [field]: value };
    patchAnslut('why_cards', cards);
  };

  const handleCreateStore = async (e) => {
    e.preventDefault();
    if (!storeForm.owner_name.trim() || !storeForm.owner_email.trim() || !storeForm.owner_password.trim()) {
      toast.error('Kontaktperson, e-post och lösenord krävs.');
      return;
    }
    if (!storeForm.store_name.trim() || !storeForm.location.trim()) {
      toast.error('Butiksnamn och adress/plats krävs.');
      return;
    }
    setCreating(true);
    try {
      const res = await adminApi.createPartnerStore({
        ...storeForm,
        auto_approve: true,
      });
      toast.success(res.data?.message || 'Partner skapad.');
      setStoreForm({
        owner_name: '',
        owner_email: '',
        owner_password: '',
        store_name: '',
        location: '',
        county: '',
        org_number: '',
        bankgiro: '',
        contact_email: '',
        contact_phone: '',
        customer_phone: '',
        description: '',
        logo_url: '',
      });
      onStoreCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa partner.');
    } finally {
      setCreating(false);
    }
  };

  const bli = partnerSettings.bli_partner || {};
  const anslut = partnerSettings.anslut || {};

  return (
    <div className="space-y-6" data-testid="admin-partners">
      <Tabs defaultValue="create">
        <TabsList className="mb-6">
          <TabsTrigger value="create" className="gap-2"><Store className="h-4 w-4" /> Skapa partner</TabsTrigger>
          <TabsTrigger value="content" className="gap-2"><FileText className="h-4 w-4" /> Partner-sidor</TabsTrigger>
        </TabsList>

        <TabsContent value="create">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-[#003B73]/10">
            <h3 className="font-bold text-[#0F172A] mb-2">Skapa ny partner manuellt</h3>
            <p className="text-sm text-gray-600 mb-6">
              Skapar butik + butiksägare (godkänd direkt). Butikssidan blir som övriga medlemmar. Publikt register-store är stängt.
            </p>
            <form onSubmit={handleCreateStore} className="grid md:grid-cols-2 gap-4 max-w-4xl">
              <div className="md:col-span-2 border-b pb-4 mb-2">
                <p className="text-sm font-semibold text-gray-700">Butiksägare (inloggning)</p>
              </div>
              <div>
                <Label>Kontaktperson *</Label>
                <Input className="mt-1" value={storeForm.owner_name} onChange={(e) => setStoreForm({ ...storeForm, owner_name: e.target.value })} />
              </div>
              <div>
                <Label>E-post (inloggning) *</Label>
                <Input type="email" className="mt-1" value={storeForm.owner_email} onChange={(e) => setStoreForm({ ...storeForm, owner_email: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Tillfälligt lösenord *</Label>
                <Input type="password" className="mt-1 max-w-md" value={storeForm.owner_password} onChange={(e) => setStoreForm({ ...storeForm, owner_password: e.target.value })} />
                <p className="text-xs text-gray-500 mt-1">Skicka lösenordet säkert till partnern (t.ex. telefon).</p>
              </div>
              <div className="md:col-span-2 border-b pb-4 mb-2 mt-2">
                <p className="text-sm font-semibold text-gray-700">Butik</p>
              </div>
              <div>
                <Label>Butiksnamn *</Label>
                <Input className="mt-1" value={storeForm.store_name} onChange={(e) => setStoreForm({ ...storeForm, store_name: e.target.value })} />
              </div>
              <div>
                <Label>Organisationsnummer</Label>
                <Input className="mt-1" value={storeForm.org_number} onChange={(e) => setStoreForm({ ...storeForm, org_number: e.target.value })} />
              </div>
              <div>
                <Label>Bankgiro</Label>
                <Input className="mt-1" value={storeForm.bankgiro} onChange={(e) => setStoreForm({ ...storeForm, bankgiro: e.target.value })} placeholder="123-4567" />
                <p className="text-xs text-gray-500 mt-1">Används vid framtida utbetalningar till butiken.</p>
              </div>
              <div>
                <Label>Adress / ort *</Label>
                <Input className="mt-1" value={storeForm.location} onChange={(e) => setStoreForm({ ...storeForm, location: e.target.value })} />
              </div>
              <div>
                <Label>Län</Label>
                <Input className="mt-1" value={storeForm.county} onChange={(e) => setStoreForm({ ...storeForm, county: e.target.value })} />
              </div>
              <div>
                <Label>Kontakt-e-post butik</Label>
                <Input className="mt-1" value={storeForm.contact_email} onChange={(e) => setStoreForm({ ...storeForm, contact_email: e.target.value })} />
              </div>
              <div>
                <Label>Kontakttelefon</Label>
                <Input className="mt-1" value={storeForm.contact_phone} onChange={(e) => setStoreForm({ ...storeForm, contact_phone: e.target.value })} />
              </div>
              <div>
                <Label>Kundväxel</Label>
                <Input className="mt-1" value={storeForm.customer_phone} onChange={(e) => setStoreForm({ ...storeForm, customer_phone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Logotyp (URL)</Label>
                <Input className="mt-1 mb-2" value={storeForm.logo_url} onChange={(e) => setStoreForm({ ...storeForm, logo_url: e.target.value })} placeholder="https://… eller ladda upp nedan" />
                <ImageUpload folder="partners" currentImage={storeForm.logo_url} onUpload={(url) => setStoreForm({ ...storeForm, logo_url: url })} aspectRatio="aspect-square" uploadLabel="Ladda upp logotyp" />
              </div>
              <div className="md:col-span-2">
                <Button type="submit" className="bg-[#003B73]" disabled={creating}>
                  {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Skapa partner
                </Button>
              </div>
            </form>
          </div>
        </TabsContent>

        <TabsContent value="content" className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-bold text-[#0F172A] mb-4">Partner-e-post (mailto på sidorna)</h3>
            <Label htmlFor="partner-email">E-postadress</Label>
            <Input id="partner-email" className="mt-1 max-w-md" value={partnerSettings.contact_email || ''} onChange={(e) => patchRoot('contact_email', e.target.value)} placeholder="partner@byggfynd.se" />
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            <h3 className="font-bold text-[#0F172A]">Sida: /bli-partner</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Hero-rubrik</Label><Input className="mt-1" value={bli.hero_title || ''} onChange={(e) => patchBli('hero_title', e.target.value)} /></div>
              <div><Label>Hero-knapp</Label><Input className="mt-1" value={bli.hero_button_label || ''} onChange={(e) => patchBli('hero_button_label', e.target.value)} /></div>
            </div>
            <div><Label>Hero-underrubrik</Label><Textarea rows={2} className="mt-1" value={bli.hero_subtitle || ''} onChange={(e) => patchBli('hero_subtitle', e.target.value)} /></div>
            <div>
              <Label>Hero-bild</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Rekommenderat format: 1920×800 px (visas i samma proportion på sidan).</p>
              <Input className="mt-1 mb-2" value={bli.hero_image_url || ''} onChange={(e) => patchBli('hero_image_url', e.target.value)} placeholder="URL eller ladda upp" />
              <ImageUpload folder="partners" currentImage={bli.hero_image_url} onUpload={(url) => patchBli('hero_image_url', url)} aspectRatio="aspect-[12/5]" uploadLabel="Ladda upp hero-bild" />
            </div>
            <div><Label>Avsnitt: rubrik</Label><Input className="mt-1" value={bli.who_title || ''} onChange={(e) => patchBli('who_title', e.target.value)} /></div>
            <div><Label>Avsnitt: text</Label><Textarea rows={3} className="mt-1" value={bli.who_text || ''} onChange={(e) => patchBli('who_text', e.target.value)} /></div>
            <div><Label>Erbjudande – rubrik</Label><Input className="mt-1" value={bli.offer_title || ''} onChange={(e) => patchBli('offer_title', e.target.value)} /></div>
            <CardFields cards={bli.offer_cards} onChange={patchBliCards} />
            <div><Label>CTA-rubrik</Label><Input className="mt-1" value={bli.cta_title || ''} onChange={(e) => patchBli('cta_title', e.target.value)} /></div>
            <div><Label>CTA-text</Label><Textarea rows={2} className="mt-1" value={bli.cta_text || ''} onChange={(e) => patchBli('cta_text', e.target.value)} /></div>
            <div><Label>CTA-knapp</Label><Input className="mt-1 max-w-md" value={bli.cta_button_label || ''} onChange={(e) => patchBli('cta_button_label', e.target.value)} /></div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
            <h3 className="font-bold text-[#0F172A]">Sida: /bli-partner/anslut-byggvaruhus</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Hero-rubrik</Label><Input className="mt-1" value={anslut.hero_title || ''} onChange={(e) => patchAnslut('hero_title', e.target.value)} /></div>
              <div><Label>Hero-knapp (mailto)</Label><Input className="mt-1" value={anslut.hero_button_label || ''} onChange={(e) => patchAnslut('hero_button_label', e.target.value)} /></div>
            </div>
            <div><Label>Hero-underrubrik</Label><Textarea rows={2} className="mt-1" value={anslut.hero_subtitle || ''} onChange={(e) => patchAnslut('hero_subtitle', e.target.value)} /></div>
            <div>
              <Label>Hero-bild</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Rekommenderat format: 1920×800 px (visas i samma proportion på sidan).</p>
              <Input className="mt-1 mb-2" value={anslut.hero_image_url || ''} onChange={(e) => patchAnslut('hero_image_url', e.target.value)} />
              <ImageUpload folder="partners" currentImage={anslut.hero_image_url} onUpload={(url) => patchAnslut('hero_image_url', url)} aspectRatio="aspect-[12/5]" uploadLabel="Ladda upp hero-bild" />
            </div>
            <div><Label>Varför – rubrik</Label><Input className="mt-1" value={anslut.why_title || ''} onChange={(e) => patchAnslut('why_title', e.target.value)} /></div>
            <CardFields cards={anslut.why_cards} onChange={patchAnslutCards} />
            <div><Label>Krav – rubrik</Label><Input className="mt-1" value={anslut.requirements_title || ''} onChange={(e) => patchAnslut('requirements_title', e.target.value)} /></div>
            <div>
              <Label>Krav (en rad per punkt)</Label>
              <Textarea rows={6} className="mt-1 font-mono text-sm" value={listToLines(anslut.requirements)} onChange={(e) => patchAnslut('requirements', linesToList(e.target.value))} />
            </div>
            <div><Label>Onboarding – rubrik</Label><Input className="mt-1" value={anslut.onboarding_title || ''} onChange={(e) => patchAnslut('onboarding_title', e.target.value)} /></div>
            <div>
              <Label>Onboarding-steg (en rad per steg)</Label>
              <Textarea rows={5} className="mt-1 font-mono text-sm" value={listToLines(anslut.onboarding_steps)} onChange={(e) => patchAnslut('onboarding_steps', linesToList(e.target.value))} />
            </div>
            <div><Label>Slut-CTA rubrik</Label><Input className="mt-1" value={anslut.final_cta_title || ''} onChange={(e) => patchAnslut('final_cta_title', e.target.value)} /></div>
            <div><Label>Slut-CTA knapp</Label><Input className="mt-1 max-w-md" value={anslut.final_cta_button_label || ''} onChange={(e) => patchAnslut('final_cta_button_label', e.target.value)} /></div>
          </div>

          <Button className="bg-[#003B73]" onClick={onSaveSettings} disabled={savingSettings}>
            {savingSettings && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Spara allt sidinnehåll
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
};
